import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import supabase from 'lib/supabase-browser'
import { apiRequest } from '../util/util'
import { toast } from 'react-hot-toast'

export function useProjectMembers({ projectId, pageIndex, pageSize }) {
  const startIndex = pageIndex * pageSize

  return useQuery(
    ['project_members', { projectId, pageIndex, pageSize }],
    async () => {
      const { data, error, count } = await supabase
        .from('project_members')
        .select('*, users(id, email)', { count: 'exact' })
        .eq('project_id', projectId)
        .range(startIndex, startIndex + pageSize - 1)

      if (error) {
        console.error('Error fetching project members:', error.message)
        throw new Error(error.message)
      }

      return { items: data, totalCount: count }
    },
    {
      keepPreviousData: true,
    }
  )
}

export function useProjectMember({ projectId, userId }) {
  return useQuery(['project_member', { projectId, userId }], () =>
    supabase
      .from('project_members')
      .select('*')
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .then(handle)
  )
}

export const useDeleteProjectMember = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ email, project_id }: { email: string; project_id: number }) => {
      const { data, error } = await supabase
        .from('project_members')
        .delete()
        .eq('email', email)
        .eq('project_id', project_id)
        .select('*')
        .single()
      if (error) throw error

      return data
    },
    {
      onSuccess: async (data) => {
        const project_id = data[0]?.project_id
        queryClient.invalidateQueries(['project_members', { project_id }])
        queryClient.invalidateQueries(['project', { project_id }])
      },
    }
  )
}

export const useUpdateProjectMember = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({
      user_id,
      project_id,
      role,
    }: {
      user_id: string
      project_id: number
      role: string
    }) => {
      const { data, error } = await supabase
        .from('project_members')
        .update({ role })
        .eq('user_id', user_id)
        .eq('project_id', project_id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    {
      onSuccess: async (data) => {
        const project_id = data[0]?.project_id
        queryClient.invalidateQueries(['project_members', { project_id }])
        queryClient.invalidateQueries(['project', { project_id }])
      },
    }
  )
}

export const useCreateProjectMember = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({
      project_id,
      user_id,
    }: {
      project_id: number
      user_id?: string
    }) => {
      const { data, error } = await supabase
        .from('project_members')
        .insert({ project_id, user_id, role: 'member', status: 'pending' })
        .select('*')
        .single()
      if (error) throw error

      return data
    },
    {
      onSuccess: async (data) => {
        const project_id = data[0]?.project_id
        queryClient.invalidateQueries(['project_members', { project_id }])
        queryClient.invalidateQueries(['project', { project_id }])
      },
    }
  )
}

export const useInviteProjectMember = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({
      email,
      projectId,
      teamId,
      sendEmail = true,
    }: {
      email: string
      projectId: number
      teamId: number
      sendEmail?: boolean
    }) => {
      const resp = await apiRequest('invite-project-member', 'POST', {
        email: email,
        projectId: projectId,
        teamId: teamId,
        sendEmail: sendEmail,
      })
      if (resp.error) {
        toast.error(resp.error)
        return resp
      } else {
        toast.success('User added to project!')
        return resp
      }
    },
    {
      onSuccess: async (data) => {
        queryClient.invalidateQueries(['project_members'])
      },
      onError: (error) => {
        console.error('Invite failed', error) // Log error in case of failure
      },
    }
  )
}

export const useHandleInvites = ({ email }) => {
  return useQuery(['handle-invites', { email }], async () => {
    const resp = await apiRequest('handle-invites', 'POST', {
      email,
    })
    return resp
  })
}

export const useAcceptTeamInvite = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({
      email,
      teamId,
      userId,
      projectIds,
    }: {
      email: string
      teamId: number
      userId: string
      projectIds: number[]
    }) => {
      if (!teamId || !userId || !projectIds || !email) {
        throw new Error('Invalid request')
      }
      const resp = await apiRequest('accept-team-invite', 'POST', {
        email,
        teamId,
        userId,
        projectIds,
      })
      return resp
    },
    {
      onSuccess: async (data) => {
        queryClient.invalidateQueries()
      },
    }
  )
}

export const useAcceptProjectInvite = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({
      email,
      projectId,
      userId,
    }: {
      email: string
      projectId: number
      userId: string
    }) => {
      const resp = await apiRequest('accept-project-invite', 'POST', {
        email,
        projectId,
        userId,
      })
      return resp
    },
    {
      onSuccess: async (data) => {
        queryClient.invalidateQueries(['projects'])
      },
    }
  )
}

export const useIsUserInvitedToProject = ({ email, projectId }) => {
  return useQuery(
    ['is-user-invited-to-project', { email, projectId }],
    async () => {
      if (!email || !projectId) return false
      const resp = await apiRequest('is-user-invited-to-project', 'POST', {
        email,
        projectId,
      })
      return resp
    }
  )
}

export const getUserPendingTeamInvite = async (email) => {
  const { data: invitationData, error } = await supabase
    .from('project_members')
    .select('project_id')
    .eq('email', email)
    .eq('status', 'pending')
  if (error) {
    return false
  }
  return invitationData[0]
}

function handle(response) {
  if (response.error) throw response.error
  return response.data
}
